import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CAR_MODELS } from '../../config/carModels';

const CarModelsDropdown = () => {
  const [hoveredModel, setHoveredModel] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const dropdownContentRef = useRef(null);

  const carModelsArray = Object.values(CAR_MODELS);

  const handleModelClick = (e, modelId) => {
    e.stopPropagation();
    e.preventDefault();
    setIsLoading(true);
    
    // Small delay to show loader, then navigate
    setTimeout(() => {
      navigate(`/customize?model=${modelId}`);
      // Reset after navigation
      setIsLoading(false);
      setIsOpen(false);
    }, 300);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside and handle body overflow
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the button AND the dropdown content
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideContent = dropdownContentRef.current && !dropdownContentRef.current.contains(event.target);
      
      if (isOutsideButton && isOutsideContent) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <div ref={dropdownRef} className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleDropdown}
          className={`text-sm font-medium flex items-center gap-1 ${isOpen ? 'bg-accent' : ''}`}
        >
          Bilmodeller
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {isOpen && createPortal(
        <>
          {/* Combined overlay wrapper */}
          <div 
            className="fixed inset-0 z-[9998]"
            style={{ top: '56px' }}
            onClick={(e) => {
              // Only close if clicking directly on this div, not its children
              if (e.target === e.currentTarget) {
                setIsOpen(false);
              }
            }}
          >
            {/* Dark background */}
            <div className="absolute inset-0 bg-black/70 pointer-events-none" />
            
            {/* Dropdown container */}
            <div 
              ref={dropdownContentRef}
              className="relative bg-background border-t border-b shadow-lg pt-8 pb-12"
            >
              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="text-sm font-medium">Laddar designer...</p>
                  </div>
                </div>
              )}
              
              <div className="container">
                <div className="max-w-7xl mx-auto mb-8 text-left">
                  <h2 className="text-2xl font-bold mb-2">Börja med att välja din bilmodell</h2>
                  <p className="text-muted-foreground text-sm">Välj den bilmodell du vill designa dekaler för</p>
                </div>
                <div className="grid grid-cols-4 gap-6 max-w-7xl mx-auto">
                  {carModelsArray.map((model) => (
                    <Card
                      key={model.id}
                      className={`group transition-all overflow-hidden ${
                        isLoading 
                          ? 'cursor-not-allowed opacity-50' 
                          : 'cursor-pointer hover:scale-105 hover:shadow-lg'
                      }`}
                      onMouseEnter={() => !isLoading && setHoveredModel(model.id)}
                      onMouseLeave={() => setHoveredModel(null)}
                      onClick={(e) => !isLoading && handleModelClick(e, model.id)}
                    >
                      <CardContent className="p-4">
                        <div className="text-center space-y-3">
                          <div className="aspect-[4/3] bg-muted rounded-md overflow-hidden relative">
                            <img
                              src={model.previewImage}
                              alt={model.name}
                              className="w-full h-full object-contain p-4"
                            />
                            {hoveredModel === model.id && (
                              <div className="absolute inset-0 bg-background/95 flex items-center justify-center animate-in fade-in-0 zoom-in-95 duration-200">
                                <Button size="sm" variant="default">
                                  Börja designa
                                </Button>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{model.name}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default CarModelsDropdown;

